/**
 * data/repos/activitiesRepo — repositório da área `activities` (TASK-14).
 *
 * Resolve a fonte via `data/source.ts`:
 * - área real (`VITE_API_REAL_AREAS` contém `activities`) → GET /v1/activities;
 * - caso contrário → mock tipado (`data/mock.ts`).
 *
 * Mapeamento ActivityResponse (backend) → ActivityItem (portal):
 *
 * type:
 *   Backend: "meeting" | "follow_up" | "call" | "email" | "task"
 *   Portal:  mesmo valor (a união de ActivityType inclui todos os 5 valores)
 *
 * priority:
 *   Backend: "low" | "medium" | "high"
 *   Portal:  "low" | "med"    | "high"   ("medium" → "med")
 *
 * state (derivado de completedAt + dueAt — o backend NÃO envia estado visual):
 *   completedAt presente OU status ∈ {completed, cancelled} → "done"
 *   dueAt < início do dia corrente                          → "overdue"
 *   dueAt no dia corrente                                   → "due"
 *   dueAt > dia corrente                                    → "upcoming"
 *
 * accountName / ownerName: AUSENTES — backend retorna apenas GUIDs (accountId, ownerId).
 * A UI exibe "—" quando esses campos são undefined.
 */

import { apiClient } from "../../services/ApiClient.js";
import { resolveSource } from "../source.js";
import { owners as mockOwners } from "../mock.js";
import type {
  ActivityItem,
  ActivityState,
  ActivityPriority,
  ActivityType,
  ActivityStatus,
  ActivityNature,
  ActivityQuadrant,
  ActivityParticipant,
  ParticipantKind,
  StaleReviewAction,
  CreateActivityInput,
  UpdateActivityInput,
  MyDayResponse,
  TaskSettings,
  PrioritizeResult,
  ReviewStaleInput,
  RecurrenceScope,
  NextActivitySuggestion,
} from "../../features/activities/types.js";
import { mapPriorityToBackend } from "../../features/activities/types.js";

// ---------------------------------------------------------------------------
// DTO do backend activity-management (GET /v1/activities)
// ---------------------------------------------------------------------------

/**
 * DTO que espelha o contrato `ActivityResponse` do backend.
 * Todos os campos são camelCase (JSON padrão do backend .NET).
 *
 * Campos adicionados em TASK-29 (task-management):
 *   nature, quadrant, isUrgent, isImportant, suggestedUrgent,
 *   migrationCount, isStale, pinnedDate, suggestedAction, scheduledAt.
 */
interface ActivityApiResponse {
  readonly id: string;
  /** Tipo canônico: "meeting" | "follow_up" | "call" | "email" | "task" */
  readonly type: string;
  readonly title: string;
  readonly description?: string | null;
  /** ISO 8601 — prazo de vencimento (legado / projeção de compat) */
  readonly dueAt: string;
  /** Status canônico: "pending" | "in_progress" | "completed" | "cancelled" | "missed" | "archived" */
  readonly status: string;
  /** Prioridade canônica: "low" | "medium" | "high" (derivada do quadrante) */
  readonly priority: string;
  readonly ownerId: string;
  readonly buId: string;
  readonly tenantId: string;
  readonly opportunityId?: string | null;
  readonly accountId?: string | null;
  /** GUID do parceiro vinculado — opcional (Fase 5). */
  readonly partnerId?: string | null;
  /** Preenchido pelo backend quando status === "completed" */
  readonly completedAt?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;

  // ── Campos de task-management (TASK-29) ─────────────────────────────────
  /** Natureza derivada do agendamento: "task" | "appointment". */
  readonly nature?: string | null;
  /** Quadrante de Eisenhower derivado: "Q1" | "Q2" | "Q3" | "Q4". */
  readonly quadrant?: string | null;
  /** Urgência configurada pelo usuário. */
  readonly isUrgent?: boolean | null;
  /** Importância configurada pelo usuário. */
  readonly isImportant?: boolean | null;
  /** Urgência sugerida pelo backend (prazo iminente). */
  readonly suggestedUrgent?: boolean | null;
  /** Contador de migrações automáticas. */
  readonly migrationCount?: number | null;
  /** Indica se a atividade está encalhada (migrationCount >= threshold). */
  readonly isStale?: boolean | null;
  /** Data de pin (YYYY-MM-DD) para "Meu Dia", ou null. */
  readonly pinnedDate?: string | null;
  /** Ação sugerida para revisão de encalhada (presente em staleReview do me/day). */
  readonly suggestedAction?: string | null;
  /** Data+hora agendada (ISO 8601 UTC) quando nature === "appointment". */
  readonly scheduledAt?: string | null;

  // ── Campos de recorrência (BC-recurrence) ─────────────────────────────────
  /** GUID da regra de recorrência; null quando a atividade não pertence a série. */
  readonly recurrenceRuleId?: string | null;
  /** Data-âncora (YYYY-MM-DD) desta ocorrência dentro da série. */
  readonly recurrenceAnchor?: string | null;
  /** Indica se a ocorrência foi editada individualmente (exceção da série). */
  readonly isSeriesException?: boolean | null;

  // ── Notas rich-text e convidados (Fase 4 — modal rico) ────────────────────
  /** HTML das notas, já sanitizado pelo backend. */
  readonly descriptionHtml?: string | null;
  /** Convidados/participantes vinculados à atividade. */
  readonly participants?: readonly ParticipantApiDto[] | null;

  // ── Lembretes de atividade (Fase 6) ─────────────────────────────────────
  /** Antecedência do lembrete em minutos; null = usa o default do tenant. */
  readonly reminderMinutesBefore?: number | null;
}

/** DTO de participante retornado por ActivityResponse.participants. */
interface ParticipantApiDto {
  readonly id: string;
  readonly kind: string;
  readonly userId?: string | null;
  readonly email?: string | null;
  readonly displayName?: string | null;
}

/** Mapeia um DTO de participante do backend para o shape do portal. */
function mapApiParticipant(dto: ParticipantApiDto): ActivityParticipant {
  return {
    id: dto.id,
    kind: dto.kind as ParticipantKind,
    ...(dto.userId != null ? { userId: dto.userId } : {}),
    ...(dto.email != null ? { email: dto.email } : {}),
    ...(dto.displayName != null ? { displayName: dto.displayName } : {}),
  };
}

/** Resposta paginada de GET /v1/activities */
interface PagedActivitiesResponse {
  readonly items: ActivityApiResponse[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

/** DTO de GET /v1/activities/me/day (TASK-29). */
interface MyDayApiResponse {
  readonly staleReview: ActivityApiResponse[];
  readonly migrated: ActivityApiResponse[];
  readonly today: ActivityApiResponse[];
  readonly appointments: ActivityApiResponse[];
  /** Concluídas hoje — bucket novo (Item 7 / quickwins de UX). Ausente em respostas antigas. */
  readonly completedToday?: ActivityApiResponse[];
}

/** DTO de GET/PUT /v1/task-settings (TASK-29). */
interface TaskSettingsApiResponse {
  readonly migrationThreshold: number;
  readonly calendarMode: string;
  readonly urgencyAutoSuggest: boolean;
  readonly reminderMinutesBeforeDefault: number;
}

/** Resultado de PATCH /{id}/prioritize (TASK-29). */
interface PrioritizeApiResponse {
  readonly activityId: string;
  readonly quadrant: string;
  readonly priority: string;
}

// ---------------------------------------------------------------------------
// Mapeamento de priority: "medium" (backend) → "med" (portal)
// ---------------------------------------------------------------------------

function mapPriority(raw: string): ActivityPriority {
  if (raw === "medium") return "med";
  if (raw === "high") return "high";
  return "low";
}

// ---------------------------------------------------------------------------
// Derivação de state visual a partir de completedAt + status + dueAt
// ---------------------------------------------------------------------------

/**
 * Deriva o estado visual do portal a partir dos campos do backend.
 *
 * Regras (em ordem de precedência):
 * 1. completedAt presente → "done"  (atividade concluída com registro de conclusão)
 * 2. status ∈ {completed, cancelled} → "done"  (terminal sem completedAt explícito)
 * 3. dueAt anterior ao início do dia corrente → "overdue"
 * 4. dueAt no dia corrente → "due"
 * 5. dueAt posterior ao dia corrente → "upcoming"
 *
 * Exportada para testes unitários.
 */
export function deriveActivityState(
  dueAt: string,
  status: string,
  completedAt: string | null | undefined,
): ActivityState {
  // Regra 1: completedAt presente
  if (completedAt) return "done";

  // Regra 2: status terminal
  if (status === "completed" || status === "cancelled") return "done";

  // Regras 3-5: compara dueAt com o dia corrente (sem horário)
  const due = new Date(dueAt);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);

  if (due < todayStart) return "overdue";
  if (due < tomorrowStart) return "due";
  return "upcoming";
}

// ---------------------------------------------------------------------------
// Mapeamento ActivityApiResponse → ActivityItem
// ---------------------------------------------------------------------------

/**
 * Mapeia um DTO de atividade do backend para o shape do portal.
 *
 * Campos omitidos / nullable:
 *   - accountName: o backend retorna apenas `accountId` (GUID);
 *     a UI exibe "—" quando ausente (campo opcional em ActivityItem).
 *   - ownerName: o backend retorna apenas `ownerId` (GUID);
 *     a UI exibe "—" quando ausente (campo opcional em ActivityItem).
 *
 * Campos preservados para gestão e admin:
 *   - ownerId, buId, opportunityId, accountId, description, status
 */
export function mapApiActivity(dto: ActivityApiResponse): ActivityItem {
  return {
    id: dto.id,
    type: dto.type as ActivityType,
    title: dto.title,
    description: dto.description ?? null,
    dueDate: dto.scheduledAt ?? dto.dueAt,
    state: deriveActivityState(dto.scheduledAt ?? dto.dueAt, dto.status, dto.completedAt),
    priority: mapPriority(dto.priority),
    status: dto.status as ActivityStatus,
    ownerId: dto.ownerId,
    buId: dto.buId,
    opportunityId: dto.opportunityId ?? null,
    accountId: dto.accountId ?? null,
    partnerId: dto.partnerId ?? null,
    // accountName e ownerName: resolvidos fora do repo via /v1/users

    // ── Campos task-management (TASK-29) ──────────────────────────────────
    ...(dto.nature != null ? { nature: dto.nature as ActivityNature } : {}),
    ...(dto.quadrant != null ? { quadrant: dto.quadrant as ActivityQuadrant } : {}),
    ...(dto.isUrgent != null ? { isUrgent: dto.isUrgent } : {}),
    ...(dto.isImportant != null ? { isImportant: dto.isImportant } : {}),
    ...(dto.suggestedUrgent != null ? { suggestedUrgent: dto.suggestedUrgent } : {}),
    ...(dto.migrationCount != null ? { migrationCount: dto.migrationCount } : {}),
    ...(dto.isStale != null ? { isStale: dto.isStale } : {}),
    pinnedDate: dto.pinnedDate ?? null,
    ...(dto.suggestedAction != null
      ? { suggestedAction: dto.suggestedAction as StaleReviewAction }
      : {}),
    scheduledAt: dto.scheduledAt ?? null,

    // ── Campos de recorrência (BC-recurrence) ──────────────────────────────
    recurrenceRuleId: dto.recurrenceRuleId ?? null,
    recurrenceAnchor: dto.recurrenceAnchor ?? null,
    ...(dto.isSeriesException != null ? { isSeriesException: dto.isSeriesException } : {}),

    // ── Notas rich-text e convidados (Fase 4) ──────────────────────────────
    descriptionHtml: dto.descriptionHtml ?? null,
    ...(dto.participants != null
      ? { participants: dto.participants.map(mapApiParticipant) }
      : {}),

    // ── Lembretes de atividade (Fase 6) ────────────────────────────────────
    reminderMinutesBefore: dto.reminderMinutesBefore ?? null,
  };
}

// ---------------------------------------------------------------------------
// Tipos de parâmetros de listagem
// ---------------------------------------------------------------------------

export interface ListActivitiesParams {
  /** Filtra pelo GUID do responsável (ownerId) */
  readonly owner?: string | null;
  /** Filtra pelo tipo de atividade (ex.: "call", "email", "meeting", "task", "follow_up") */
  readonly type?: string | null;
  /** Filtra pelo status de atividade (ex.: "pending", "in_progress", "completed", "cancelled") */
  readonly status?: string | null;
  /** Quando true, retorna apenas atividades com dueAt anterior à data corrente e status não-terminal */
  readonly overdue?: boolean;
  /**
   * Filtra por oportunidade vinculada.
   * Suportado como param de query pelo backend (opportunityId).
   */
  readonly opportunityId?: string | null;
  /**
   * Filtra por parceiro vinculado.
   * Suportado como param de query pelo backend (partnerId, Fase 5).
   */
  readonly partnerId?: string | null;
  /**
   * Filtra por BU.
   * O backend NÃO suporta filtro de buId via query param (implícito via RLS).
   * Quando informado, a filtragem é feita client-side após receber os itens.
   */
  readonly buId?: string | null;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface ActivitiesPage {
  readonly items: readonly ActivityItem[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

// ---------------------------------------------------------------------------
// listActivities
// ---------------------------------------------------------------------------

export async function listActivities(params: ListActivitiesParams = {}): Promise<ActivitiesPage> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    const query: Record<string, string | number | boolean> = {};
    if (params.owner) query["owner"] = params.owner;
    if (params.type) query["type"] = params.type;
    if (params.status) query["status"] = params.status;
    if (params.overdue !== undefined) query["overdue"] = params.overdue;
    if (params.opportunityId) query["opportunityId"] = params.opportunityId;
    if (params.partnerId) query["partnerId"] = params.partnerId;
    if (params.page) query["page"] = params.page;
    if (params.pageSize) query["pageSize"] = params.pageSize;

    const dto = await apiClient.get<PagedActivitiesResponse>(
      "/activities",
      Object.keys(query).length > 0 ? query : undefined,
    );

    let items = (dto.items ?? []).map(mapApiActivity);

    // Filtragem client-side por buId (o backend não suporta este param).
    if (params.buId) {
      const buId = params.buId;
      items = items.filter((a) => !a.buId || a.buId === buId);
    }

    return {
      items,
      total: dto.total ?? items.length,
      page: dto.page ?? 1,
      pageSize: dto.pageSize ?? items.length,
    };
  }

  // Modo mock — mantém compatibilidade com os dados existentes em data/mock.ts
  if (!source.mock) throw new Error(`[repo] mock não disponível para área: ${source.area}`);
  const mock = source.mock;
  const items = mock.activities.map((a, _idx) => ({
    id: a.id,
    type: a.type as ActivityItem["type"],
    title: a.title,
    accountName: mock.accounts.find((acc) => acc.id === a.account)?.name ?? a.account,
    ownerName: mockOwners[a.owner]?.name ?? a.owner,
    ownerId: a.owner,
    dueDate: a.due,
    state: a.state as ActivityState,
    status: (a.state === "done" ? "completed" : "pending") as ActivityItem["status"],
    priority: a.priority as ActivityItem["priority"],
  }));

  return { items, total: items.length, page: 1, pageSize: items.length };
}

// ---------------------------------------------------------------------------
// createActivity — POST /v1/activities
// ---------------------------------------------------------------------------

/**
 * Cria uma nova atividade comercial.
 *
 * Mapeamento de prioridade: portal "med" → backend "medium".
 * Retorna o id da atividade criada (do corpo 201 ou do header Location).
 */
/**
 * Determina o campo de agendamento a enviar para o backend.
 * Precedência: dueDate (date-only, tarefa) > scheduledAt (datetime, compromisso) > dueAt (legado → scheduledAt).
 */
function resolveScheduleField(
  input: Pick<CreateActivityInput, "dueDate" | "scheduledAt" | "dueAt">,
): Record<string, string> {
  if (input.dueDate) return { dueDate: input.dueDate };
  if (input.scheduledAt) return { scheduledAt: input.scheduledAt };
  if (input.dueAt) return { scheduledAt: input.dueAt };
  return {};
}

export async function createActivity(input: CreateActivityInput): Promise<{ id: string }> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    return apiClient.create("/activities", {
      type: input.type,
      title: input.title,
      ...resolveScheduleField(input),
      ownerId: input.ownerId,
      ...(input.priority != null ? { priority: mapPriorityToBackend(input.priority) } : {}),
      ...(input.isUrgent != null ? { isUrgent: input.isUrgent } : {}),
      ...(input.isImportant != null ? { isImportant: input.isImportant } : {}),
      // descriptionHtml tem precedência: quando presente, o backend deriva `description`
      // texto-plano a partir dele — não enviamos os dois juntos.
      ...(input.descriptionHtml != null
        ? { descriptionHtml: input.descriptionHtml }
        : input.description != null && input.description !== ""
          ? { description: input.description }
          : {}),
      ...(input.opportunityId ? { opportunityId: input.opportunityId } : {}),
      ...(input.accountId ? { accountId: input.accountId } : {}),
      ...(input.partnerId ? { partnerId: input.partnerId } : {}),
      ...(input.recurrence ? { recurrence: input.recurrence } : {}),
      ...(input.participants != null ? { participants: input.participants } : {}),
      ...(input.reminderMinutesBefore != null
        ? { reminderMinutesBefore: input.reminderMinutesBefore }
        : {}),
    });
  }

  // Mock: devolve id sintético
  return { id: `mock-activity-${Date.now()}` };
}

// ---------------------------------------------------------------------------
// updateActivity — PUT /v1/activities/{id}
// ---------------------------------------------------------------------------

/**
 * Anexa `?scope=` ao path quando um escopo de recorrência é informado.
 * Usado por updateActivity/deleteActivity — ignorado pelo backend quando a
 * atividade-alvo não pertence a uma série.
 */
function withScopeQuery(path: string, scope?: RecurrenceScope): string {
  return scope ? `${path}?scope=${scope}` : path;
}

/**
 * Atualiza uma atividade existente.
 *
 * O backend exige o campo `type` no corpo do PUT (UpdateActivityRequest).
 * Mapeamento de prioridade: portal "med" → backend "medium".
 * `scope` (this|this_and_following|all_series) é enviado via query param quando
 * a atividade pertence a uma série de recorrência; default do backend é "this".
 * Retorna void (204 No Content).
 */
export async function updateActivity(
  id: string,
  input: UpdateActivityInput,
  scope?: RecurrenceScope,
): Promise<void> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    await apiClient.put<void>(withScopeQuery(`/activities/${id}`, scope), {
      type: input.type,
      title: input.title,
      ...resolveScheduleField(input),
      ...(input.priority != null ? { priority: mapPriorityToBackend(input.priority) } : {}),
      ...(input.isUrgent != null ? { isUrgent: input.isUrgent } : {}),
      ...(input.isImportant != null ? { isImportant: input.isImportant } : {}),
      // descriptionHtml tem precedência: quando presente, o backend deriva `description`
      // texto-plano a partir dele — não enviamos os dois juntos.
      ...(input.descriptionHtml != null
        ? { descriptionHtml: input.descriptionHtml }
        : input.description != null
          ? { description: input.description }
          : {}),
      // Chave OMITIDA = "preserve o valor atual" no backend (LDG-0090/LDG-0043): estes campos
      // não têm controle no modal de edição, e reenviá-los com o contexto da tela relinkaria a
      // atividade ao parceiro/conta da página aberta. Chave PRESENTE com null = esvaziar de
      // propósito — por isso `reminderMinutesBefore` distingue `undefined` (não mexer) de `null`
      // ("usar o padrão da conta").
      ...(input.opportunityId != null ? { opportunityId: input.opportunityId } : {}),
      ...(input.accountId != null ? { accountId: input.accountId } : {}),
      ...(input.partnerId != null ? { partnerId: input.partnerId } : {}),
      ...(input.participants != null ? { participants: input.participants } : {}),
      ...(input.reminderMinutesBefore !== undefined
        ? { reminderMinutesBefore: input.reminderMinutesBefore }
        : {}),
    });
    return;
  }

  // Mock: no-op
  void id; void input; void scope;
}

// ---------------------------------------------------------------------------
// completeActivity — PATCH /v1/activities/{id}/complete
// ---------------------------------------------------------------------------

/** Resultado retornado pelo backend ao concluir uma atividade. */
export interface CompleteActivityResult {
  readonly activityId: string;
  readonly completedAt: string;
  readonly wasAlreadyCompleted: boolean;
  /**
   * Sugestão de próxima atividade — presente quando a atividade concluída
   * está vinculada a uma oportunidade aberta (Fase 5). O backend calcula a
   * sugestão; o portal apenas repassa para oferecer o agendamento.
   */
  readonly suggestion?: NextActivitySuggestion | null;
}

/**
 * Conclui uma atividade (idempotente).
 * Retorna a resposta de conclusão do backend.
 */
export async function completeActivity(id: string): Promise<CompleteActivityResult> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    const dto = await apiClient.patch<{
      activityId: string;
      completedAt: string;
      wasAlreadyCompleted: boolean;
      suggestion?: { opportunityId: string; accountId?: string | null } | null;
    }>(`/activities/${id}/complete`, {});
    return {
      activityId: dto.activityId,
      completedAt: dto.completedAt,
      wasAlreadyCompleted: dto.wasAlreadyCompleted,
      ...(dto.suggestion != null
        ? {
            suggestion: {
              opportunityId: dto.suggestion.opportunityId,
              ...(dto.suggestion.accountId != null ? { accountId: dto.suggestion.accountId } : {}),
            },
          }
        : {}),
    };
  }

  // Mock: retorna resultado sintético, sem sugestão
  return {
    activityId: id,
    completedAt: new Date().toISOString(),
    wasAlreadyCompleted: false,
  };
}

// ---------------------------------------------------------------------------
// rescheduleActivity — PATCH /v1/activities/{id}/reschedule
// ---------------------------------------------------------------------------

/**
 * Reagenda uma atividade com nova data de vencimento.
 * Retorna void (backend retorna 200 com { activityId, newDueAt } — ignoramos).
 */
export async function rescheduleActivity(id: string, dueAt: string): Promise<void> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    await apiClient.patch<unknown>(`/activities/${id}/reschedule`, { dueAt });
    return;
  }

  // Mock: no-op
  void id; void dueAt;
}

// ---------------------------------------------------------------------------
// deleteActivity — DELETE /v1/activities/{id}
// ---------------------------------------------------------------------------

/**
 * Exclui uma atividade.
 * `scope` (this|this_and_following|all_series) é enviado via query param quando
 * a atividade pertence a uma série de recorrência; default do backend é "this".
 * Retorna void (204 No Content).
 */
export async function deleteActivity(id: string, scope?: RecurrenceScope): Promise<void> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    await apiClient.delete<void>(withScopeQuery(`/activities/${id}`, scope));
    return;
  }

  // Mock: no-op
  void id; void scope;
}

// ---------------------------------------------------------------------------
// getActivitiesInRange — GET /v1/activities/me/range (aba Calendário)
// ---------------------------------------------------------------------------

/**
 * getActivitiesInRange — GET /v1/activities/me/range?from=&to= (aba Calendário).
 *
 * Retorna as atividades do usuário autenticado dentro do intervalo [from, to]
 * (datas YYYY-MM-DD, inclusive). O backend limita o intervalo a no máximo 62
 * dias (400 caso contrário) — o chamador (CalendarView, mês visível) nunca
 * excede esse limite.
 *
 * Escopo: usuário autenticado (mesmo escopo de getMyDay). Diferente de
 * listActivities, não é paginado — retorna a lista simples de ActivityResponse.
 */
export async function getActivitiesInRange(
  from: string,
  to: string,
): Promise<readonly ActivityItem[]> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    const items = await apiClient.get<ActivityApiResponse[]>("/activities/me/range", {
      from,
      to,
    });
    return (items ?? []).map(mapApiActivity);
  }

  // Modo mock: os dados de data/mock.ts usam rótulos de data não-ISO ("Hoje · 15:00"),
  // incompatíveis com filtragem por intervalo real — segue o mesmo padrão de
  // getMyDay (mock) e retorna lista vazia.
  return [];
}

// ---------------------------------------------------------------------------
// Novos endpoints task-management (TASK-29)
// ---------------------------------------------------------------------------

/**
 * getMyDay — GET /v1/activities/me/day (TASK-29 / REQ-14).
 *
 * Retorna as 4 seções do "Meu Dia" com ordenação Q1→Q4 vinda do servidor.
 * Frontend NÃO re-ordena (REQ-11).
 */
export async function getMyDay(): Promise<MyDayResponse> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    const dto = await apiClient.get<MyDayApiResponse>("/activities/me/day");
    return {
      staleReview: (dto.staleReview ?? []).map(mapApiActivity),
      migrated: (dto.migrated ?? []).map(mapApiActivity),
      today: (dto.today ?? []).map(mapApiActivity),
      appointments: (dto.appointments ?? []).map(mapApiActivity),
      completedToday: (dto.completedToday ?? []).map(mapApiActivity),
    };
  }

  // Mock: retorna seções vazias
  return { staleReview: [], migrated: [], today: [], appointments: [], completedToday: [] };
}

/**
 * prioritizeActivity — PATCH /v1/activities/{id}/prioritize (TASK-29 / REQ-09).
 *
 * Define os eixos urgente/importante e recebe o quadrante e prioridade derivados.
 */
export async function prioritizeActivity(
  id: string,
  isUrgent: boolean,
  isImportant: boolean,
): Promise<PrioritizeResult> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    const dto = await apiClient.patch<PrioritizeApiResponse>(
      `/activities/${id}/prioritize`,
      { isUrgent, isImportant },
    );
    return {
      activityId: id,
      quadrant: dto.quadrant as PrioritizeResult["quadrant"],
      priority: mapPriority(dto.priority) as PrioritizeResult["priority"],
    };
  }

  // Mock: deriva quadrante localmente
  const quadrant = isUrgent && isImportant ? "Q1"
    : !isUrgent && isImportant ? "Q2"
    : isUrgent && !isImportant ? "Q3"
    : "Q4";
  const priority = quadrant === "Q1" ? "high" : quadrant === "Q4" ? "low" : "med";
  return { activityId: id, quadrant, priority };
}

/**
 * reviewStaleActivity — POST /v1/activities/{id}/review (TASK-29 / REQ-12).
 *
 * Executa a ação de revisão de encalhada:
 *   - do_now: retoma (pending)
 *   - schedule: converte em compromisso com scheduledAt
 *   - delegate: reatribui a assigneeId
 *   - eliminate: arquiva
 *
 * Retorna 409 quando a atividade não está encalhada (isStale=false).
 */
export async function reviewStaleActivity(
  id: string,
  input: ReviewStaleInput,
): Promise<void> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    await apiClient.patch<unknown>(`/activities/${id}/review`, {
      action: input.action,
      ...(input.scheduledAt ? { scheduledAt: input.scheduledAt } : {}),
      ...(input.assigneeId ? { assigneeId: input.assigneeId } : {}),
    });
    return;
  }

  // Mock: no-op
  void id; void input;
}

/**
 * reassignActivity — PATCH /v1/activities/{id}/reassign (TASK-29).
 *
 * Reatribui a atividade a outro membro da BU.
 * Restrito a Gestor BU e Tenant Admin (REQ-07/Q7).
 */
export async function reassignActivity(id: string, assigneeId: string): Promise<void> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    await apiClient.patch<unknown>(`/activities/${id}/reassign`, { assigneeId });
    return;
  }

  // Mock: no-op
  void id; void assigneeId;
}

/**
 * reopenActivity — PATCH /v1/activities/{id}/reopen (TASK-29).
 *
 * Reabre uma atividade concluída (completed → pending).
 * Preserva migrationCount.
 */
export async function reopenActivity(id: string): Promise<void> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    await apiClient.patch<unknown>(`/activities/${id}/reopen`, {});
    return;
  }

  // Mock: no-op
  void id;
}

/**
 * pinActivity — PATCH /v1/activities/{id}/pin (TASK-29).
 *
 * Define ou remove o pin de "Meu Dia" para uma atividade.
 * pinnedDate = null remove o pin; string YYYY-MM-DD define o dia de exibição.
 */
export async function pinActivity(id: string, pinnedDate: string | null): Promise<void> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    await apiClient.patch<unknown>(`/activities/${id}/pin`, { pinnedDate });
    return;
  }

  // Mock: no-op
  void id; void pinnedDate;
}

/**
 * getReassignmentQueue — GET /v1/activities/reassignment-queue (TASK-29).
 *
 * Retorna tarefas abertas de usuários desativados da BU do gestor.
 * Restrito a Gestor BU e Tenant Admin.
 */
export async function getReassignmentQueue(): Promise<readonly ActivityItem[]> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    const items = await apiClient.get<ActivityApiResponse[]>("/activities/reassignment-queue");
    return (items ?? []).map(mapApiActivity);
  }

  // Mock: lista vazia
  return [];
}

/**
 * getTaskSettings — GET /v1/task-settings (TASK-29 / REQ-19).
 *
 * Retorna as configurações de tarefas do tenant.
 * Visível apenas para Tenant Admin.
 */
export async function getTaskSettings(): Promise<TaskSettings> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    const dto = await apiClient.get<TaskSettingsApiResponse>("/task-settings");
    return {
      migrationThreshold: dto.migrationThreshold,
      calendarMode: dto.calendarMode as TaskSettings["calendarMode"],
      urgencyAutoSuggest: dto.urgencyAutoSuggest,
      reminderMinutesBeforeDefault: dto.reminderMinutesBeforeDefault,
    };
  }

  // Mock: valores padrão
  return {
    migrationThreshold: 3,
    calendarMode: "business_days",
    urgencyAutoSuggest: true,
    reminderMinutesBeforeDefault: 15,
  };
}

/**
 * updateTaskSettings — PUT /v1/task-settings (TASK-29 / REQ-19).
 *
 * Salva as configurações de tarefas do tenant.
 * Restrito a Tenant Admin.
 */
export async function updateTaskSettings(settings: TaskSettings): Promise<void> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    await apiClient.put<void>("/task-settings", {
      migrationThreshold: settings.migrationThreshold,
      calendarMode: settings.calendarMode,
      urgencyAutoSuggest: settings.urgencyAutoSuggest,
      reminderMinutesBeforeDefault: settings.reminderMinutesBeforeDefault,
    });
    return;
  }

  // Mock: no-op
  void settings;
}

// ---------------------------------------------------------------------------
// getOpportunitiesWithoutFollowup — GET /v1/activities/opportunities-without-followup (Fase 5)
// ---------------------------------------------------------------------------

/** DTO de GET /v1/activities/opportunities-without-followup. */
interface OpportunitiesWithoutFollowupApiResponse {
  readonly opportunityIds: readonly string[];
}

/**
 * getOpportunitiesWithoutFollowup — GET /v1/activities/opportunities-without-followup (Fase 5).
 *
 * Retorna os GUIDs das oportunidades abertas da BU do usuário corrente que NÃO
 * possuem atividade futura não-terminal vinculada. Usado pelo Kanban de
 * Pipeline para marcar visualmente os cards sem follow-up agendado.
 */
export async function getOpportunitiesWithoutFollowup(): Promise<readonly string[]> {
  const source = await resolveSource("activities");

  if (source.kind === "api") {
    const dto = await apiClient.get<OpportunitiesWithoutFollowupApiResponse>(
      "/activities/opportunities-without-followup",
    );
    return dto.opportunityIds ?? [];
  }

  // Mock: nenhuma oportunidade marcada
  return [];
}

// Reexporta para consumidores que tipam a sugestão de próxima atividade.
export type { NextActivitySuggestion };
